import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Badge, Button, Spinner } from 'react-bootstrap';
import {
    Users, Clock, CreditCard, TrendingUp, ArrowRight,
    UserCheck, Briefcase, FileText, Bell, IndianRupee,
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import axiosInstance from '../../api/axiosConfig';

const StatCard = ({ title, value, subtitle, icon: Icon, color, path, loading }) => {
    const navigate = useNavigate();
    return (
        <Card
            className="border-0 shadow-sm h-100"
            onClick={() => path && navigate(path)}
            style={{ cursor: path ? 'pointer' : 'default', transition: 'transform 0.15s' }}
            onMouseEnter={e => path && (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseLeave={e => path && (e.currentTarget.style.transform = 'none')}
        >
            <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <small className="text-muted fw-bold text-uppercase"
                            style={{ fontSize: '0.7rem', letterSpacing: '0.06rem' }}>
                            {title}
                        </small>
                        {loading
                            ? <div className="mt-2"><Spinner size="sm" variant={color}/></div>
                            : <>
                                <h2 className="fw-bold mb-0 mt-1">{value}</h2>
                                {subtitle && (
                                    <div className="text-muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>
                                        {subtitle}
                                    </div>
                                )}
                              </>
                        }
                    </div>
                    <div className={`p-3 bg-${color} bg-opacity-10 rounded-circle`}>
                        <Icon className={`text-${color}`} size={24} />
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

const typeConfig = {
    new_employee:  { icon: UserCheck, color: '#10b981', label: 'New Employee' },
    leave_request: { icon: FileText,  color: '#f59e0b', label: 'Leave Request' },
    attendance:    { icon: Clock,     color: '#6366f1', label: 'Attendance'   },
};

const AdminOverview = ({ user }) => {
    const [stats,    setStats]    = useState(null);
    const [activity, setActivity] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const isSuperAdmin = user?.role === 'super-admin';

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const statsEndpoint = isSuperAdmin
                ? '/superadmin/global-stats'
                : '/dashboard/stats';

            const [statsRes, activityRes] = await Promise.allSettled([
                axiosInstance.get(statsEndpoint),
                axiosInstance.get('/dashboard/activity'),
            ]);

            if (statsRes.status === 'fulfilled' && statsRes.value.data.success) {
                const d = statsRes.value.data.data;
                setStats(isSuperAdmin ? (d?.stats || d) : d);
            }
            if (activityRes.status === 'fulfilled' && activityRes.value.data.success) {
                setActivity(activityRes.value.data.data || []);
            }
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, [isSuperAdmin]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const fmt = (n) => parseInt(n || 0).toLocaleString('en-IN');
    const cur = (n) => new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(n || 0);

    // Build stat cards based on role
    const cards = isSuperAdmin
        ? [
            {
                title:    'Total Companies',
                value:    fmt(stats?.total_companies),
                subtitle: stats?.signups_this_month
                    ? `+${stats.signups_this_month} this month`
                    : null,
                icon:  Briefcase,
                color: 'primary',
                path:  '/superadmin/companies',
            },
            {
                title: 'Total Employees',
                value: fmt(stats?.total_employees),
                icon:  Users,
                color: 'success',
                path:  null,
            },
            {
                title: 'Active Subscriptions',
                value: fmt(stats?.active_subscriptions),
                icon:  CreditCard,
                color: 'info',
                path:  '/superadmin/plans',
            },
            {
                // CHANGED: was "Total Payouts" (employee salary payouts)
                // NOW: "Total Revenue" = money PeoplesDock collected from signups + renewals
                title:    'Total Revenue',
                value:    cur(stats?.total_revenue),
                subtitle: stats?.revenue_this_month
                    ? `${cur(stats.revenue_this_month)} this month`
                    : null,
                icon:  IndianRupee,
                color: 'warning',
                path:  '/superadmin/plans',
            },
          ]
        : [
            {
                title: 'Total Employees',
                value: fmt(stats?.total_employees),
                icon:  Users,
                color: 'primary',
                path:  '/employees',
            },
            {
                title: 'Present Today',
                value: fmt(stats?.present_today),
                icon:  Clock,
                color: 'success',
                path:  '/attendance',
            },
            {
                title: 'Pending Leaves',
                value: fmt(stats?.pending_leaves),
                icon:  FileText,
                color: 'warning',
                path:  '/leave',
            },
            {
                title: 'Open Jobs',
                value: fmt(stats?.open_jobs),
                icon:  Briefcase,
                color: 'info',
                path:  '/ats',
            },
          ];

    const timeAgo = (ts) => {
        const diff = (Date.now() - new Date(ts)) / 1000;
        if (diff < 60)   return 'just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400)return `${Math.floor(diff / 3600)}h ago`;
        return new Date(ts).toLocaleDateString('en-IN', { day:'numeric', month:'short' });
    };

    return (
        <div className="p-3 p-md-4 animate-fade-in">
            {/* Header */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div>
                    <h3 className="fw-bold mb-1">
                        {isSuperAdmin ? 'Platform Overview' : 'Organisation Overview'}
                    </h3>
                    <p className="text-muted mb-0 small">
                        {isSuperAdmin
                            ? 'Live platform stats across all tenants'
                            : `Insights for ${user?.company_name || 'your company'}`
                        }
                    </p>
                </div>
                {!isSuperAdmin && (
                    <Button variant="primary" as={Link} to="/payroll/management"
                        className="d-flex align-items-center gap-2">
                        <CreditCard size={18}/> Run Monthly Payroll
                    </Button>
                )}
                {isSuperAdmin && (
                    <Button variant="primary" as={Link} to="/superadmin/companies"
                        className="d-flex align-items-center gap-2">
                        <Briefcase size={18}/> Manage Companies
                    </Button>
                )}
            </div>

            {/* Stat cards */}
            <Row className="g-3 mb-4">
                {cards.map((c, i) => (
                    <Col key={i} xs={6} lg={3}>
                        <StatCard {...c} loading={loading}/>
                    </Col>
                ))}
            </Row>

            {/* Activity feed */}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold mb-0">
                            <Bell size={16} className="me-2 text-primary"/>Recent Activity
                        </h6>
                        <Link
                            to={isSuperAdmin ? '/superadmin/logs' : '/attendance'}
                            className="text-decoration-none small d-flex align-items-center gap-1 text-muted">
                            View All <ArrowRight size={14}/>
                        </Link>
                    </div>
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" size="sm"/>
                        </div>
                    ) : activity.length > 0 ? (
                        <div className="d-flex flex-column gap-2">
                            {activity.slice(0, 8).map((item, i) => {
                                const cfg  = typeConfig[item.type] || typeConfig.attendance;
                                const Icon = cfg.icon;
                                return (
                                    <div key={i}
                                        className="d-flex align-items-start gap-3 p-2 rounded-3"
                                        style={{ background: '#f8fafc' }}>
                                        <div style={{
                                            width: 34, height: 34, borderRadius: '50%',
                                            flexShrink: 0, background: `${cfg.color}18`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Icon size={16} style={{ color: cfg.color }}/>
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="small fw-bold">{item.label}</div>
                                            <div className="text-muted" style={{ fontSize: 12 }}>{item.action}</div>
                                        </div>
                                        <div className="text-muted" style={{ fontSize: 11, flexShrink: 0 }}>
                                            {timeAgo(item.ts)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-4 text-muted small">No recent activity</div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminOverview;
